/**
 * @file project-mixin.ts
 *
 * Owns project and session state and wires the event listeners that drive
 * project lifecycle. On connect it creates an initial project, checks IDB for
 * unsaved sessions from a previous tab, and registers handlers for all
 * project-related events.
 *
 * All handler logic is delegated to app-project.ts; this mixin is responsible
 * only for state ownership, listener registration, and providing the correct
 * 'this' context to each handler.
 *
 * Requires: ModalMixinMembers, ErrorMixinMembers, InitQueueMixinMembers
 * Exposes: unsavedChanges, isSaving, pendingAction, currentProject, frozenAt,
 *          sessionSnapshotNeeded, autoSaveDebounceTimer, unsavedSessionProject,
 *          customData (getter), setCustomData(patch), setCustomDataDefaults(defaults), revertCustomData()
 */
import * as ProjectHandlers from '../app-project.ts';

import { createNewProject, currentProjectData, type Project } from '../../../data/projectManagement.ts';
// import { getProjectsForApp } from '../../../logic/db.ts';
import { liveAppData } from '../../../store/store.ts';
import { registerListeners, unregisterListeners } from '../../../logic/utils.ts';
import { AS_CONFIGURATOR_EVENTS } from '../../../data/events.ts';

import { merge as _merge } from 'lodash';
import { type Constructor } from './mixin-types-utils.ts';
import type { ModalMixinMembers } from './modal-mixin.ts';
import type { ErrorMixinMembers } from './error-mixin.ts';
import type { InitQueueMixinMembers } from './init-queue-mixin.ts';

/** Safety-net delay for releasing init dirty-suppression if a configurator never
 *  reports ready (e.g. init failure), so the app can't stay permanently clean. */
const INIT_RELEASE_FALLBACK_MS = 10000;

/** Surface added by ProjectMixin. */
export interface ProjectMixinMembers {
  unsavedChanges: boolean;
  /**
   * Clears unsavedChanges through the post-load "settle" window. New / Restore /
   * cold boot leave the project unmodified by definition, but the async configure
   * they trigger emits as-input-change *after* the synchronous clear, which would
   * re-dirty a naive one-shot reset. Clears now, after the microtask flush, and
   * once more after the next configurator settle.
   */
  markPristine(): void;
  isSaving: boolean;
  /**
   * Tracks a deferred action that needs to run after a save completes (or be
   * cancelled if the user aborts the save). `null` means no action is pending.
   *
   * - `'new-session'`    — user clicked New while there were unsaved changes;
   *                        confirmed-save leads to createAndSetNewProject.
   * - `'home'`           — user clicked Home while there were unsaved changes;
   *                        confirmed-save leads to _redirectToStorefront.
   * - `'save-and-close'` — user clicked "Save and close" (e.g. GrabCAD modal);
   *                        confirmed-save dispatches as-close-app on window.
   *
   * Set by the action initiator; cleared either by the as-project-saved listener
   * (success path) or by cancel-save-project / cancel-exit-app (cancel path).
   * See docs/as-app.md § pendingAction state machine.
   */
  pendingAction: 'new-session' | 'home' | 'save-and-close' | null;
  currentProject: Project | null;
  /** Timestamp (ms) set when the tab becomes hidden; used to detect long pauses
   *  and decide whether to restore the paramate session on return. */
  frozenAt: number | null;
  /** Marks that a paramate session snapshot is needed on the next visibility
   *  change or autosave tick. Set when any input changes. */
  sessionSnapshotNeeded: boolean;
  autoSaveDebounceTimer: number | null;
  /** Holds an unsaved session from a previous tab until the user chooses to
   *  restore or discard it. Cleared once the decision is made. */
  unsavedSessionProject: Project | null;
  /** Live customData for the current project. */
  readonly customData: Record<string, any>;
  /** Deep-merge patch into customData and mark the project dirty. Set silent=true to skip dirty marking and history. */
  setCustomData(patch: Record<string, any>, silent?: boolean): void;
  /** Store defaults and deep-merge missing keys into customData (existing values win at every level). */
  setCustomDataDefaults(defaults: Record<string, any>): void;
  /** Wipe customData back to the stored defaults. */
  revertCustomData(): void;
  /** Subscribe to customData changes. Returns an unsubscribe function. */
  customDataSubscribe(callback: (data: Record<string, any>) => void): () => void;
  /**
   * Register a hook run (awaited, in registration order) just before a project is
   * serialized — the place to flush the app's latest state into customData. Returns
   * an opaque handle for removeBeforeSaveHook(); do NOT rely on indices.
   */
  addBeforeSaveHook(fn: () => void | Promise<void>): BeforeSaveHookHandle;
  /** Remove a previously registered before-save hook by its handle. */
  removeBeforeSaveHook(handle: BeforeSaveHookHandle): void;
  /** Remove all before-save hooks. */
  clearBeforeSaveHooks(): void;
  /** Run all before-save hooks in registration order (awaited). */
  runBeforeSaveHooks(): Promise<void>;
}

/** Opaque handle returned by addBeforeSaveHook. */
export type BeforeSaveHookHandle = symbol;

export const ProjectMixin = <T extends Constructor<HTMLElement & ModalMixinMembers & ErrorMixinMembers & InitQueueMixinMembers>>(Base: T) =>
  class ProjectMixin extends Base {
    private _unsavedChanges: boolean = false;
    get unsavedChanges(): boolean { return this._unsavedChanges; }
    
    set unsavedChanges(value: boolean) {
      this._unsavedChanges = value;
      liveAppData.unsavedChanges = value;
    }

    /**
     * Gate that suppresses dirty-marking. Starts FALSE: a fresh app is held clean
     * through the init cycle so the configurator's startup configure/param-sync
     * can't dirty a brand-new session. Released exactly once when init finishes
     * (see _releaseInitTracking, driven by as-intrn-configurator-ready). New /
     * Restore re-close it for the duration of their own settle (markPristine).
     * While false, markDirty() is a no-op.
     */
    private _dirtyTrackingReady = false;
    private _settleTimer: number | null = null;
    private _onSettleConfigured: (() => void) | null = null;
    /** Fallback so a configurator that never signals ready can't leave the app
     *  permanently un-dirtyable (see connectedCallback). */
    private _initReleaseTimer: number | null = null;

    /**
     * Mark the session dirty in response to a genuine user edit. A no-op while
     * dirty-tracking is suppressed (the init cycle, or markPristine's New/Restore
     * settle window), so startup/reset param-sync events don't dirty a fresh
     * session. The single entry point for setting unsavedChanges = true.
     */
    markDirty(): void {
      if (!this._dirtyTrackingReady) return;
      this.unsavedChanges = true;
      this.sessionSnapshotNeeded = true;
    }

    /**
     * Release the one-time init suppression: init has finished, so clear any
     * startup settle dirt and begin tracking genuine edits. Unlike New/Restore,
     * cold boot triggers NO follow-up configure to wait on — so we must not wait
     * for a future after-configure (that "next configure" would be the user's own
     * first edit, which is exactly the bug this avoids). Anything an app does that
     * dirties *after* init is a real change; such an app is responsible for
     * clearing it itself (e.g. unsavedChanges = false). Idempotent.
     */
    private _releaseInitTracking(): void {
      if (this._initReleaseTimer !== null) { clearTimeout(this._initReleaseTimer); this._initReleaseTimer = null; }
      this.unsavedChanges = false;
      this.sessionSnapshotNeeded = false;
      this._dirtyTrackingReady = true;
    }

    /**
     * Reset to a clean session and suppress dirty-tracking until it settles.
     * Called by New / Restore: each clears the session and then *itself* triggers
     * an async configure (as-reset-configurator / restoreSession) whose param-sync
     * would otherwise re-dirty the fresh session. So we clear and gate off
     * markDirty() until that triggered configure completes (one-shot
     * after-configure listener, with a timeout fallback). Cold boot does NOT use
     * this — it has no follow-up configure and releases directly via
     * _releaseInitTracking.
     */
    markPristine(): void {
      this._resumeDirtyTracking();          // cancel any in-flight settle from a prior call
      this._dirtyTrackingReady = false;
      this.unsavedChanges = false;
      this.sessionSnapshotNeeded = false;

      this._onSettleConfigured = () => this._resumeDirtyTracking();
      window.addEventListener(AS_CONFIGURATOR_EVENTS.PARAMATE_AFTER_CONFIGURE, this._onSettleConfigured, { once: true });
      this._settleTimer = window.setTimeout(() => this._resumeDirtyTracking(), 4000);
    }

    /** Resume dirty-tracking and tear down any pending settle listener/timer. */
    private _resumeDirtyTracking(): void {
      if (this._settleTimer !== null) { clearTimeout(this._settleTimer); this._settleTimer = null; }
      if (this._onSettleConfigured) {
        window.removeEventListener(AS_CONFIGURATOR_EVENTS.PARAMATE_AFTER_CONFIGURE, this._onSettleConfigured);
        this._onSettleConfigured = null;
      }
      this._dirtyTrackingReady = true;
    }

    isSaving: boolean = false;
    pendingAction: 'new-session' | 'home' | 'save-and-close' | null = null;
    currentProject: Project | null = null;
    frozenAt: number | null = null;
    sessionSnapshotNeeded: boolean = false;
    autoSaveDebounceTimer: number | null = null;
    unsavedSessionProject: Project | null = null;
    
    private _customDataDefaults: Record<string, any> = {};

    // asApp.customData 
    get customData(): Record<string, any> {
      return currentProjectData.customData;
    }
    // TODO: review if needed
    setCustomData(patch: Record<string, any>, silent = false): void {
      currentProjectData.setCustomData(_merge({}, currentProjectData.customData, patch), silent);
    }


    setCustomDataDefaults(defaults: Record<string, any>): void {
      this._customDataDefaults = defaults;
      currentProjectData.setCustomData(_merge({}, defaults, currentProjectData.customData), true);
    }

    revertCustomData(): void {
      currentProjectData.setCustomData({ ...this._customDataDefaults }, true);
    }

    customDataSubscribe(callback: (data: Record<string, any>) => void): () => void {
      return currentProjectData.subscribe(callback);
    }

    // Insertion-ordered registry keyed by opaque handles (a Map preserves insertion
    // order), so hooks run in registration order and removal stays stable as hooks
    // are added/removed.
    private _beforeSaveHooks: Map<BeforeSaveHookHandle, () => void | Promise<void>> = new Map();

    addBeforeSaveHook(fn: () => void | Promise<void>): BeforeSaveHookHandle {
      const handle: BeforeSaveHookHandle = Symbol('before-save-hook');
      this._beforeSaveHooks.set(handle, fn);
      return handle;
    }

    removeBeforeSaveHook(handle: BeforeSaveHookHandle): void {
      this._beforeSaveHooks.delete(handle);
    }

    clearBeforeSaveHooks(): void {
      this._beforeSaveHooks.clear();
    }

    async runBeforeSaveHooks(): Promise<void> {
      for (const fn of this._beforeSaveHooks.values()) {
        await fn();
      }
    }

    private _projectSelfListeners: Record<string, EventListener> = {};
    private _projectWindowListeners: Record<string, EventListener> = {};
    private _projectDocumentListeners: Record<string, EventListener> = {};

    async connectedCallback() {
      await super.connectedCallback?.();

      const appId = (this as any).appId as string;
      this.currentProject = createNewProject(appId);
      liveAppData.currentProject = this.currentProject;
      liveAppData.defaultProjectHandlingEnabled = !(this as unknown as HTMLElement).hasAttribute('disable-session-handlers');

      // IDB-based unsaved session recovery disabled alongside autosave.
      // if (!simpleProjects) {
      //   const allProjects = await getProjectsForApp(appId);
      //   const unsavedProjects = allProjects.filter((p: Project) => !p.metadata.savedAt);
      //   if (unsavedProjects.length > 0) {
      //     this.unsavedSessionProject = unsavedProjects[0];
      //     this.openModal('restore-session', 'as-modal-restore-session');
      //   }
      // }

      this._projectSelfListeners = {
        'new-session':       () => ProjectHandlers.handleNewSession(this as any),
        'save-session':      () => ProjectHandlers.handleSaveSession(this as any),
        'restore-session':   () => ProjectHandlers.handleRestoreSession(this as any),
        // TODO: review if needed: 
        // IDB project CRUD disabled — file-based save/restore only for now.
        // 'as-project-select': (e) => ProjectHandlers.handleProjectSelect(this as any, e),
        // 'as-project-save':   (e) => ProjectHandlers.handleProjectSave(this as any, e),
        // 'as-project-rename': (e) => ProjectHandlers.handleProjectRename(this as any, e),
        // 'as-project-delete': (e) => ProjectHandlers.handleProjectDelete(this as any, e),
        'restore-session-file': (e) => ProjectHandlers.handleRestoreSessionFile(this as any, e),
        'as-input-change': () => {
          this.markDirty();
          ProjectHandlers.scheduleAutoSave(this as any);
        },
        'as-project-saved': () => {
          this.unsavedChanges = false;
          if (this.pendingAction === 'save-and-close') {
            this.pendingAction = null;
            this.modalOverlay?.close();
            window.dispatchEvent(new CustomEvent('as-close-app'));
          }
        },
      } as Record<string, EventListener>;

      this._projectWindowListeners = {
        'as-project-change': () => {
          this.markDirty();
          ProjectHandlers.scheduleAutoSave(this as any);
        },
        'as-project-load-request': (e) => ProjectHandlers.handleProjectLoadRequest(this as any, e),
        // Cold boot: the session is held clean through init (dirtyTrackingReady
        // starts false). Once the configurator signals ready, release tracking so
        // a fresh session doesn't start "unsaved" and the user's first real edit
        // is tracked. No settle-window past ready — see _releaseInitTracking.
        'as-intrn-configurator-ready': () => this._releaseInitTracking(),
        // The configurator's keepalive hit a dead session (machine slept past the
        // session TTL with the tab visible, so no visibilitychange fired) — route
        // it into the same expired-session flow as sleep detection.
        'as-session-lost': () => ProjectHandlers.handleSessionExpired(this as any, 0),
      } as Record<string, EventListener>;

      this._projectDocumentListeners = {
        'visibilitychange': () => ProjectHandlers.handleVisibilityChange(this as any),
      } as Record<string, EventListener>;

      registerListeners(this, this._projectSelfListeners);
      registerListeners(window, this._projectWindowListeners);
      registerListeners(document, this._projectDocumentListeners);

      // Drive the one-time init release. A configurator app stays held until its
      // as-intrn-configurator-ready fires; an app with no configurator has no init
      // cycle, so enable tracking now. The timer is a safety net for a configurator
      // that never reports ready (e.g. init failure) so the app can't get stuck
      // permanently un-dirtyable.
      if ((this as unknown as HTMLElement).querySelector('as-configurator')) {
        this._initReleaseTimer = window.setTimeout(() => this._releaseInitTracking(), INIT_RELEASE_FALLBACK_MS);
      } else {
        this._releaseInitTracking();
      }

      // IDB project list disabled.
      // ProjectHandlers.refreshProjectsList(this as any);

      ProjectHandlers.initSleepDetection(this as any);
    }

    disconnectedCallback() {
      if (this._initReleaseTimer !== null) { clearTimeout(this._initReleaseTimer); this._initReleaseTimer = null; }
      this._resumeDirtyTracking();
      unregisterListeners(this, this._projectSelfListeners);
      unregisterListeners(window, this._projectWindowListeners);
      unregisterListeners(document, this._projectDocumentListeners);
      ProjectHandlers.teardownSleepDetection();
      super.disconnectedCallback?.();
    }
  };
